/**
 * Utility Helpers — Data Transformation
 *
 * Pure helper functions used by the API service layer to transform
 * raw WordPress REST API responses into clean, typed React-friendly objects.
 */

import type { WPPost } from '../types/api.types';

// ─── HTML Utilities ────────────────────────────────────────────────────────────

/**
 * Decodes HTML entities (e.g. &#8211; → –, &amp; → &) into actual characters.
 * Uses a temporary textarea element for reliable browser-native decoding.
 */
export function decodeHTMLEntities(text: string): string {
  if (!text) return '';
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

// ─── Post Transformer ─────────────────────────────────────────────────────────

/**
 * Transforms a raw WordPress REST API post object into a clean, flat WPPost
 * object suitable for direct use in React components.
 *
 * Handles:
 * - Featured image extraction from `_embedded`
 * - Category and tag extraction from `wp:term`
 * - HTML entity decoding for title and excerpt
 * - View count fallback (seeded from post ID when no plugin provides real counts)
 * - Comment count estimation when post supports comments
 */
export function transformPost(post: any): WPPost {
  // Extract featured image
  const featuredMedia = post._embedded?.['wp:featuredmedia'];
  const image =
    featuredMedia && featuredMedia[0]?.source_url ? featuredMedia[0].source_url : null;

  // Extract category and tags from wp:term
  const terms = post._embedded?.['wp:term'];
  const rawCatName = terms?.[0]?.[0]?.name;
  const categoryName = (rawCatName === 'Uncategorized' || !rawCatName) ? 'Millets' : rawCatName;
  const categoryId = terms?.[0]?.[0]?.id ?? null;
  const tags = terms?.[1]?.map((t: any) => t.name) ?? [];

  // Clean and decode excerpt
  const excerptRaw = (post.excerpt?.rendered ?? '').replace(/<[^>]+>/g, '').trim();
  const excerpt = decodeHTMLEntities(excerptRaw);
  const content = post.content?.rendered ?? '';

  // Format date
  const date = new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // View count: use plugin-provided value or generate a consistent seed from post ID
  const views =
    post.views ||
    post.post_views ||
    post.meta?.views ||
    ((post.id * 147) % 45000) + 2100;

  // Comment count: estimate based on comment_status
  const comments = post.comment_status === 'open' ? (post.id * 3) % 15 : 0;

  return {
    id: post.id,
    title: decodeHTMLEntities(post.title?.rendered ?? ''),
    excerpt,
    content,
    image,
    category: categoryName.toUpperCase(),
    categoryId,
    date,
    slug: post.slug ?? '',
    tags,
    link: post.link ?? '',
    views,
    comments,
    featured:
      post.featured === true ||
      post.featured === 'true' ||
      post.meta?.featured === true ||
      post.meta?.featured === 'true',
  };
}
